import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { FileText, Wand2, Check, ArrowRight, AlertCircle } from 'lucide-react'

// 央国企黑话库
const CORP_SLANG = {
  '主导项目': '主持/牵头负责',
  '赋能': '支持/指导/帮扶',
  '协同': '配合',
  '沉淀': '积累',
  '抓手': '切入点/着力点',
  '闭环': '完善/落实',
  '底层逻辑': '根本原因',
  '顶层设计': '总体规划',
  '对齐': '沟通/协调',
  '落地': '实施/执行',
  '颗粒度': '细致程度',
  '组合拳': '综合措施',
  '赛道': '领域/方向',
  '痛点': '问题/困难',
  '矩阵': '体系/框架',
  '裂变': '扩展/发展',
  '链路': '流程/过程',
  '中台': '支撑平台',
  '漏斗': '筛选过程',
  '复盘': '总结/回顾'
}

// 简历模板
const RESUME_TEMPLATES = {
  personalSummary: {
    before: '主导过3个大型项目，赋能团队提升效率50%，擅长协同多方资源完成复杂任务。',
    after: '主持3个重点项目，支持团队提升工作效率50%，善于配合各方力量完成复杂工作。'
  },
  experience: {
    before: '在XX公司实习期间，主导产品迭代，协同技术团队完成需求落地。',
    after: '在XX公司实习期间，牵头产品优化工作，配合技术部门完成需求实施。'
  }
}

const ResumeOptimizePage = () => {
  const [activeTab, setActiveTab] = useState('optimize')
  const [inputText, setInputText] = useState('')
  const [optimizedText, setOptimizedText] = useState('')
  const [detectedWords, setDetectedWords] = useState([])

  const handleOptimize = () => {
    if (!inputText.trim()) return
    
    let text = inputText
    const found = []
    
    // 替换黑话
    Object.entries(CORP_SLANG).forEach(([internet, corp]) => {
      if (text.includes(internet)) {
        found.push({ original: internet, replacement: corp })
        text = text.split(internet).join(corp)
      }
    })
    
    setOptimizedText(text)
    setDetectedWords(found)
  }

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-8">
        <h1 className="section-title">简历优化</h1>
        <p className="section-subtitle">将互联网黑话翻译为央国企风格表述</p>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card p-6 md:p-8">
        {/* 黑话库展示 */}
        <div className="mb-8">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center">
            <Wand2 size={20} className="mr-2 text-primary-600" />
            央国企黑话库（常用替换）
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(CORP_SLANG).slice(0, 12).map(([original, replacement]) => (
              <div key={original} className="p-3 bg-gray-50 rounded-lg text-sm">
                <div className="text-red-500 line-through mb-1">{original}</div>
                <div className="text-green-600 font-medium">{replacement}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 优化工具 */}
        <div className="mb-8">
          <h3 className="font-bold text-gray-900 mb-4">简历文本优化</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">原始文本（互联网风格）</label>
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="粘贴你的简历描述，如：主导过XX项目，赋能团队..."
                className="input-field h-40 resize-none"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">优化后（央国企风格）</label>
              <textarea
                value={optimizedText}
                readOnly
                placeholder="点击优化按钮查看结果..."
                className="input-field h-40 resize-none bg-gray-50"
              />
            </div>
          </div>
          <button
            onClick={handleOptimize}
            className="mt-4 btn-primary flex items-center justify-center"
          >
            <Wand2 size={18} className="mr-2" />
            一键优化
          </button>
        </div>

        {/* 检测到的词汇 */}
        {detectedWords.length > 0 && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center mb-2">
              <AlertCircle size={18} className="text-yellow-600 mr-2" />
              <span className="font-medium text-yellow-800">检测到 {detectedWords.length} 个需要替换的词汇</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {detectedWords.map((word, i) => (
                <span key={i} className="px-3 py-1 bg-yellow-200 text-yellow-800 rounded-full text-sm">
                  {word.original} → {word.replacement}
                </span>
              ))}
            </div>
          </motion.div>
        )}

        {/* STAR法则说明 */}
        <div className="p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <h4 className="font-bold text-primary-800 mb-2">STAR法则改写建议</h4>
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <div className="text-center">
              <div className="w-10 h-10 bg-primary-200 rounded-full flex items-center justify-center mx-auto mb-2 font-bold text-primary-700">S</div>
              <p className="font-medium text-primary-800">情境</p>
              <p className="text-primary-600">描述背景</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-primary-200 rounded-full flex items-center justify-center mx-auto mb-2 font-bold text-primary-700">T</div>
              <p className="font-medium text-primary-800">任务</p>
              <p className="text-primary-600">明确目标</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-primary-200 rounded-full flex items-center justify-center mx-auto mb-2 font-bold text-primary-700">A</div>
              <p className="font-medium text-primary-800">行动</p>
              <p className="text-primary-600">具体做法</p>
            </div>
            <div className="text-center">
              <div className="w-10 h-10 bg-primary-200 rounded-full flex items-center justify-center mx-auto mb-2 font-bold text-primary-700">R</div>
              <p className="font-medium text-primary-800">结果</p>
              <p className="text-primary-600">量化成果</p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

export default ResumeOptimizePage
